import type { FilterState } from "@/lib/catalog";
import { defaultFilter, genrePool, isFilterActive, yearOptions } from "@/lib/catalog";

interface FilterBarProps {
  filter: FilterState;
  onChange: (next: FilterState) => void;
  showGenre?: boolean;
}

const regions = ["全部", "日本", "韩国"];
const sorts = ["最新", "最热", "评分"];

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-label font-medium whitespace-nowrap cursor-pointer transition-colors border ${
        active
          ? "bg-primary-600 border-primary-600 text-background-50"
          : "bg-background-50 border-background-200 text-foreground-700 hover:border-background-300 hover:text-foreground-950"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterBar({ filter, onChange, showGenre = true }: FilterBarProps) {
  const rows = [
    { key: "year" as const, label: "年份", options: ["全部", ...yearOptions.map(String)] },
    { key: "region" as const, label: "地区", options: regions },
    { key: "sort" as const, label: "排序", options: sorts },
  ];

  const active = isFilterActive(filter);

  return (
    <div className="rounded-lg bg-background-50 border border-background-200/80 p-4 md:p-5">
      <div className="flex flex-col gap-3.5">
        {rows.map((row) => (
          <div key={row.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="shrink-0 w-12 text-xs font-label font-semibold text-foreground-500">{row.label}</span>
            <div className="flex flex-wrap gap-2">
              {row.options.map((option) => (
                <Chip
                  key={option}
                  active={filter[row.key] === option}
                  onClick={() => onChange({ ...filter, [row.key]: option })}
                >
                  {option}
                </Chip>
              ))}
            </div>
          </div>
        ))}

        {showGenre && (
          <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
            <span className="shrink-0 w-12 text-xs font-label font-semibold text-foreground-500 sm:mt-1.5">题材</span>
            <div className="flex flex-wrap gap-2">
              {["全部", ...genrePool].map((g) => (
                <Chip key={g} active={filter.genre === g} onClick={() => onChange({ ...filter, genre: g })}>
                  {g}
                </Chip>
              ))}
            </div>
          </div>
        )}
      </div>

      {active && (
        <div className="mt-4 pt-3.5 border-t border-background-200 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-foreground-500">已应用筛选条件</p>
          <button
            type="button"
            onClick={() => onChange({ ...defaultFilter })}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-background-100 text-xs font-label font-semibold text-foreground-700 hover:bg-background-200 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-refresh-line w-3.5 h-3.5 flex items-center justify-center" />
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}